<template>
  <PageView :page-reference="object_page_ref" :current-object-type="default_object_type_obj">
    <template #header>
      <PagesTabs />
      <TripleMenu
        v-model:current-template="current_template"
        :set-edit-mode="setEditMode"
        :edit-mode-active="edit_mode_active"
        :quick-actions="top_right_menu_quick_actions.filter((i) => !i.hidden)"
        :components-actions="top_right_menu_components_actions"
        :page-reference="object_page_ref" />
      <PageBreadCrumb
        :page-reference="object_page_ref"
        :is-page-title-hidden="true"
        :page-title="$route.params?.page ? $route.params?.page : `OBJECT PROFILE`"
        :page-bread-crumb="breads" />
    </template>
    <template #body>
      <LayoutContainer
        v-model:current-template="current_template"
        :set-edit-mode="setEditMode"
        :edit-mode-active="edit_mode_active"
        class="mt-3 !ml-6 !h-[calc(100vh-180px)]"
        :page-reference="object_page_ref" />
    </template>
  </PageView>
</template>

<script>
import TripleMenu from "@/components/shared/TripleMenu.vue"
import PagesTabs from "@/components/shared/PagesTabs"

// import ObjectsView from "@/components/shared/object-view/ObjectsView.vue"
import PageView from "@/components/shared/pages/PageView.vue"

import { PAGE_REFERENCES, VIEW_TYPES } from "@/store/constants/references"

import LayoutContainer from "@/components/shared/pages/LayoutContainer.vue"

import PageBreadCrumb from "@/components/shared/pages/PageBreadCrumb.vue"
import ModuleIcon from "@/components/shared/icons/ModuleIcon.vue"

import { Record } from "@/models/record"

import { markRaw } from "vue"
import {
  Plus,
  User,
  Users,
  ClipboardList,
  Mail,
  Folder,
  TrendingUp,
  MessagesSquare,
  FileDown,
  ArrowLeft,
  List,
  Tag,
  ChartNoAxesColumnDecreasing
} from "lucide-vue-next"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
import { utils } from "@/store/helpers"

export default {
  name: "ObjectProfile",
  components: {
    PageView,
    PagesTabs,
    // ObjectsView,
    LayoutContainer,
    TripleMenu,
    PageBreadCrumb,
    // eslint-disable-next-line vue/no-unused-components
    ArrowLeft: markRaw(ArrowLeft)
  },
  inject: ["isModuleActivated"],
  beforeRouteLeave() {
    // called when the route that renders this component is about to be navigated away from.
    if (this?.edit_mode_active) {
      const answer = window.confirm(this.$t("You have unsaved changes, layout will be reverted if you don't save"))
      if (!answer) {
        return false
      } else {
        this.edit_mode_active = false
      }
    }
  },
  beforeRouteUpdate: (to, _from) => {
    if (!to.params.page) {
      return to.path + "/overview"
    }
  },
  data() {
    return {
      current_template: null,
      default_object_type_obj: { type: "default" },
      breads: [],
      edit_mode_active: false

      // smart table outputs

      // selected_range: null,
    }
  },
  computed: {
    designated_record() {
      if (this.$route.params.object_type == "opportunity") {
        return this.$store.state.ui.currentCompany
      }
      return this.$store.state.ui.currentCompany.parent
    },
    object_page_ref() {
      if (this.$route.params.object_type == "contact") {
        return PAGE_REFERENCES.CONTACT_PROFILE
      }
      return PAGE_REFERENCES.OBJECT_PROFILE
    },
    top_right_menu_quick_actions() {
      const common_actions = [
        {
          name: this.$t("Create a Group"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.CREATE_NEW_GROUP, this.$route.params.object_type)
          },
          icon: Folder,
          disabled: false
        },
        {
          name: this.$t("Assign Tag"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.ASSIGN_TAGS, {
              record: this.$store.state.ui.currentCompany,
              object_type: this.$route.params.object_type
            })
          },
          icon: Tag,
          disabled: false
        },
        {
          name: this.$t("Create a Custom Metric"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.CREATE_CUSTOM_METRIC, {
              object_type: this.$route.params.object_type,
              view_type: "table"
            })
          },
          icon: ChartNoAxesColumnDecreasing,
          disabled: false
        }
      ]

      const contact_or_opportunity_specific_actions = [
        {
          name: this.$t("Create a Plan"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.CREATE_PLAN, {
              record: this.designated_record,
              contact: this.$route.params.object_type === "contact" ? this.$store.state.ui.currentCompany : null
            })
          },
          icon: List,
          disabled: false
        },
        {
          name: this.$t("Create a Goal"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.CREATE_GOAL, {
              record: this.designated_record,
              contact: this.$route.params.object_type === "contact" ? this.$store.state.ui.currentCompany : null
            })
          },
          icon: TrendingUp,
          disabled: false
        },
        {
          name: this.$t("Create a Task"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.CREATE_TASK, {
              record: this.designated_record,
              contact: this.$route.params.object_type === "contact" ? this.$store.state.ui.currentCompany : null
            })
          },
          icon: ClipboardList,
          disabled: false
        },
        {
          name: this.$t("Create an Interaction"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.CREATE_INTERACTION, {
              record: this.designated_record,
              contact: this.$route.params.object_type === "contact" ? this.$store.state.ui.currentCompany : null
            })
          },
          icon: MessagesSquare,
          disabled: false
        },
        {
          name: this.$t("Send Email"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.SEND_EMAIL, {
              record: this.$route.params.object_type === "contact" ? this.$store.state.ui.currentCompany : this.designated_record
            })
          },
          icon: Mail,
          disabled: false
        },
        {
          name: this.$t("Assign Owner"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.CREATE_OWNER, {
              record: this.$store.state.ui.currentCompany
            })
          },
          icon: User,
          disabled: false
        },
        {
          name: this.$t("Assign Co-owners"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.CHANGE_CO_OWNERS, {
              record: this.$store.state.ui.currentCompany
            })
          },
          icon: Users,
          disabled: false
        },
        {
          name: this.$t("Generate NPS"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.GENERATE_EXTERNAL_SCORE, {
              records: [this.$store.state.ui.currentCompany]
            })
          },
          icon: FileDown,
          disabled: false,
          hidden: this.$route.params.object_type !== "contact"
        }
      ]

      if (this.$route.params.object_type === "opportunity") {
        return [
          {
            name: this.$t("Add Contact"),
            action: () => {
              const metrics_obj = {
                object_type: "contact",
                page_reference: PAGE_REFERENCES.CONTACT_PROFILE,
                view_type: VIEW_TYPES.FORM_CREATE
              }
              this.$emitter.emit(ALL_EMITTERS.ADD_NEW_RECORD, metrics_obj)
            },
            icon: Plus
          },
          {
            name: this.$t("Add Department"),
            action: () => {
              const metrics_obj = {
                object_type: "department",
                page_reference: PAGE_REFERENCES.COMPANY_PROFILE,
                view_type: VIEW_TYPES.FORM_CREATE
              }
              this.$emitter.emit(ALL_EMITTERS.ADD_NEW_RECORD, metrics_obj)
            },
            icon: Plus
          },
          ...contact_or_opportunity_specific_actions,
          ...common_actions
        ]
      }

      if (this.$route.params.object_type === "contact") {
        return [...contact_or_opportunity_specific_actions, ...common_actions]
      }

      return common_actions
    },

    top_right_menu_components_actions() {
      return [
        {
          name: this.$t("Edit Layout"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.EDIT_LAYOUT, {
              page_reference: this.object_page_ref,
              view_type: VIEW_TYPES.ROLLUPS,
              page_name: this.$route.params.page
            })
          },
          disabled: false,
          icon: ModuleIcon
        },
        {
          name: this.$t("Add/Remove Components"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.ADD_NEW_COMPONENT, {
              page_reference: this.object_page_ref,
              current_template: this.current_template
            })
          },
          disabled: false,
          icon: ModuleIcon
        },

        {
          name: this.current_template?.is_static ? this.$t("Reset Page") : this.$t("Delete Page"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.DELETE_PAGE, this.current_template)
          },
          icon: ModuleIcon
        }
      ]
    }
  },

  unmounted() {
    // Clear breadcrumb
    this.$store.dispatch("ui/setCurrentCompanyBreadcrumb", [])
    // remove current company from store
    this.$store.dispatch("ui/setCurrentCompany", null)
  },

  activated() {
    this.init_company(true)

    const company_profile_pages = this.$store.state.portfolio.pages_layout["company_profile"]?.filter((p) => !p.is_hidden) || []

    // Check if there are visible pages and no page in the current route
    if (!this.$route.params.page && company_profile_pages.length) {
      const first_page_id = company_profile_pages[0].id

      // Remove the last slash if present, then append the first page ID
      const new_path = `${this.$route.path.replace(/\/$/, "")}/${first_page_id}`

      // Replace the current route with the new path
      this.$router.replace(new_path)
    }
  },
  methods: {
    setEditMode(value) {
      this.edit_mode_active = value
    },
    init_company() {
      // Clear the current company in the store
      this.$store.dispatch("ui/setCurrentCompany", null)

      // Fetch the company record based on the route's company ID (cid)
      this.$store.dispatch("data/getRecord", this.$route.params.cid).then((result) => {
        const company_data = result.data
        const company_name = Record.from(company_data).name

        // Start with the base breadcrumb (Back arrow)
        this.breads = [
          {
            id: 1,
            label: " ",
            component: ArrowLeft,
            hide_arrow: true,
            is_component: true,
            onClick: () => this.$router.go(-1)
          }
        ]

        // If the company has a parent, append the parent-related breadcrumbs
        if (company_data?.parent) {
          this.breads = this.breads.concat([
            {
              id: 2,
              label: utils.getObjectTypeByType(company_data.parent_type).plural_label,
              onClick: () => this.$router.push(`/portfolio/${company_data.parent_type}`)
            },
            {
              id: 3,
              label: company_data?.parent_name,
              onClick: () => this.$router.push(`/portfolio/${company_data.parent_type}/${company_data.parent}`)
            },
            {
              id: 4,
              label: `${company_data.type == company_data.parent_type ? "Child " : ""}${utils.ObjectType(company_data.type).plural_label}`,
              onClick: () => this.$router.push(`/portfolio/${company_data.parent_type}/${company_data.parent}/${company_data.type}`)
            }
          ])
        } else {
          // If there's no parent, just append the company type breadcrumb
          this.breads.push({
            id: 2,
            label: `${company_data.type == company_data.parent_type ? "Child " : ""}${utils.ObjectType(company_data.type).plural_label}`,
            onClick: () => this.$router.push(`/portfolio/${company_data.type}`)
          })
        }

        // Append the company name breadcrumb if it exists
        if (company_name) {
          this.breads.push({
            id: company_data?.parent ? 5 : 3, // Adjust the id based on the presence of a parent
            label: company_name
          })
        }

        // Append the template name breadcrumb if it exists
        if (this.current_template?.name) {
          console.log(this.current_template)
          this.breads.push({
            id: company_data?.parent ? 6 : 4, // Adjust the id based on the presence of a parent
            label: this.current_template?.objectType
              ? utils.ObjectType(this.current_template?.objectType)?.plural_label
              : this.current_template?.slug
          })
        }

        // Set the fetched company data in the store
        this.$store.dispatch("ui/setCurrentCompany", company_data)
      })
    }
  }
}
</script>
